'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { LuArrowLeft, LuCheck, LuImport } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import ErrorToast from '@/core/components/error-toast'
import { Checkbox } from '@/core/components/form/checkbox'
import { Combobox } from '@/core/components/form/combobox'
import {
  IMPORT_FIELDS,
  IMPORT_LABEL,
} from '@/features/studio-renew/lib/studio-import'
import { importEntity } from '@/features/studio-renew/services/studio-import.service'
import {
  clearImportRuns,
  startImportConfirm,
} from '@/features/studio-renew/services/import-run.service'
import { studioSession } from '@/features/studio-renew/services/studio-session'
import { acceptImportedEntities, flushAll } from '@/features/studio-renew/sync'
import { STEPS } from '@/features/studio-renew/steps'
import { SheetHead } from '@/features/veluvit/components/overlay/layer-parts'
import { ApiError, api } from '@/features/veluvit/services/api'
import { closeTop } from '@/features/veluvit/stores/overlay.store'
import { useSessionStore } from '@/features/veluvit/stores/session.store'
import { walletSchema } from '@/features/veluvit/schemas/wallet.schema'

import type {
  ImportAnalysis,
  ImportConfirmed,
} from '@/features/studio-renew/schemas/import-result.schema'
import type { ImportRunScope } from '@/features/studio-renew/services/import-run.service'
import type { ImportTarget } from '@/features/studio-renew/services/studio-import.service'
import type { ServerImportConfirm } from '@/features/studio-renew/schemas/server-import.schema'
import type { Route } from 'next'

/** 완료 영수증의 과거 revision을 편집기에 재적용하지 않는다. */
export async function applyImportedResult(result: ImportConfirmed) {
  const session = studioSession()
  const preserved = await acceptImportedEntities([
    ...result.materials.map((row) => ({
      kind: row.kind,
      response: row.material,
    })),
    ...(result.work ? [{ kind: 'chat' as const, response: result.work }] : []),
  ])
  session.assertCurrent()
  if (result.wallet) {
    // 완료 영수증의 과거 잔액으로 현재 지갑을 되돌리지 않는다.
    const wallet = await api<unknown>('/wallet/')
      .then((value) => walletSchema.parse(value))
      .catch(() => null)
    session.assertCurrent()
    if (wallet) useSessionStore.getState().applyWallet(wallet)
  }
  return preserved
}

type Props = {
  scope: ImportRunScope
  destination: ImportTarget
  text: string
  analysisId?: string
  analysis?: ImportAnalysis
  initialResult?: ImportConfirmed
  initialPreserved?: string[]
  onBack: () => void
}
export default function ServerImportReview({
  scope,
  destination,
  text,
  analysisId,
  analysis,
  initialResult,
  initialPreserved,
  onBack,
}: Props) {
  const router = useRouter()
  const [complete, setComplete] = useState(initialResult ?? null)
  const [busy, setBusy] = useState(false)
  const [phase, setPhase] = useState<'saving' | 'refreshing'>('saving')
  const [preserved, setPreserved] = useState<string[]>(initialPreserved ?? [])
  const [frozen, setFrozen] = useState(false)
  const [error, setError] = useState('')
  const [allowUpdate, setAllowUpdate] = useState(false)
  const [lead, setLead] = useState('')
  const root = analysis?.candidates.find(
    (row) => row.candidate === destination.rootCandidate
  )
  const [originalTarget] = useState(() => importEntity(destination))
  const current = originalTarget
  const targetRevision = destination.baseRevision ?? current?.revision
  const [actions, setActions] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (analysis?.candidates ?? []).map((row) => [
        row.candidate,
        destination.id && row === root
          ? `current:${destination.id}`
          : row.recommendation === 'review'
            ? ''
            : 'auto',
      ])
    )
  )
  const alive = useRef(false)
  const lock = useRef(false)
  const controller = useRef<AbortController | null>(null)
  const receipt = useRef<ImportConfirmed | null>(null)
  const submitted = useRef<Omit<ServerImportConfirm, 'requestId'> | null>(null)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      controller.current?.abort()
    }
  }, [])
  const candidates = analysis?.candidates ?? []
  const updates = candidates.some((row) =>
    actions[row.candidate]?.startsWith('update:')
  )
  const ready =
    !!analysis &&
    candidates.every(
      (row) =>
        !!actions[row.candidate] &&
        (!row.errors.length || actions[row.candidate].startsWith('reuse:'))
    ) &&
    (!updates || allowUpdate)

  const confirm = async () => {
    if (lock.current || !analysis || !analysisId) return
    lock.current = true
    setBusy(true)
    setError('')
    setFrozen(true)
    controller.current = new AbortController()
    try {
      const session = studioSession()
      if (session.owner !== scope.owner)
        throw new Error('이식할 작가 계정이 바뀌었어요.')
      if (!submitted.current) {
        await flushAll()
        session.assertCurrent()
        if (
          destination.id &&
          importEntity(destination)?.revision !== targetRevision
        )
          throw new Error(
            '편집 원본이 바뀌었어요. 현재 저장본을 확인하고 다시 비교해 주세요.'
          )
        const selections: ServerImportConfirm['selections'] = candidates.map(
          (row): ServerImportConfirm['selections'][number] => {
            const selected = actions[row.candidate] ?? ''
            const separator = selected.indexOf(':')
            const action =
              separator === -1 ? selected : selected.slice(0, separator)
            const id = separator === -1 ? '' : selected.slice(separator + 1)
            if (action === 'current') {
              const original = importEntity(destination)
              if (!targetRevision || original?.id !== id)
                throw new Error(
                  '편집 원본의 최신 저장본을 확인한 뒤 다시 비교해 주세요.'
                )
              return {
                candidate: row.candidate,
                action: 'update',
                id,
                revision: targetRevision,
                data: row.data,
              }
            }
            if (action === 'reuse' || action === 'update') {
              const match = row.matches.find((match) => match.id === id)
              if (!match)
                throw new Error('선택한 기존 재료를 다시 확인해 주세요.')
              return {
                candidate: row.candidate,
                action,
                id: match.id,
                revision: match.revision,
                ...(action === 'update' ? { data: row.data } : {}),
              }
            }
            if (action !== 'auto' && action !== 'create')
              throw new Error('재료별 가져오기 방법을 선택해 주세요.')
            return { candidate: row.candidate, action, data: row.data }
          }
        )
        const input: Omit<ServerImportConfirm, 'requestId'> = {
          analysisId,
          selections,
        }
        if (destination.kind === 'work') {
          if (analysis.bundle.work)
            selections.push({ candidate: 'work', data: analysis.bundle.work })
          if (destination.id && !targetRevision)
            throw new Error(
              '작품의 최신 저장본을 확인한 뒤 다시 비교해 주세요.'
            )
          input.work = destination.id
            ? { action: 'update', id: destination.id, revision: targetRevision }
            : { action: 'create' }
          input.startIndexes = analysis.bundle.starts.map((_, index) => index)
          if (lead) input.leadCandidate = lead
        } else if (analysis.bundle.starts.length)
          throw new Error('시작 장면은 작품을 적용 대상으로 선택해 주세요.')
        submitted.current = input
      }
      setPhase('saving')
      const result =
        receipt.current ??
        (await startImportConfirm(scope, submitted.current, text, destination, {
          signal: controller.current.signal,
        }))
      session.assertCurrent()
      receipt.current = result
      if (!alive.current) return
      setPhase('refreshing')
      const retained = await applyImportedResult(result)
      session.assertCurrent()
      if (!alive.current) return
      setPreserved(retained)
      setComplete(result)
      try {
        clearImportRuns(scope)
      } catch {
        /* 확정 영수증을 지우지 못해도 저장 완료 사실은 유지한다. */
      }
    } catch (cause) {
      if (alive.current) {
        setError(
          receipt.current
            ? '서버에 이식은 완료됐어요. 편집할 최신 내용을 불러오지 못했으니 저장 결과를 다시 확인해 주세요.'
            : cause instanceof Error
              ? cause.message
              : '가져오기 결과를 확인하지 못했어요.'
        )
        if (
          !receipt.current &&
          (!submitted.current ||
            (cause instanceof ApiError &&
              !cause.unknownOutcome &&
              cause.status >= 400 &&
              cause.status < 500))
        ) {
          setFrozen(false)
          submitted.current = null
        }
      }
    } finally {
      lock.current = false
      if (alive.current) setBusy(false)
    }
  }

  let savedId = destination.id ?? ''
  if (complete)
    savedId =
      destination.kind === 'work'
        ? (complete.work?.id ?? savedId)
        : (complete.materials.find((row) => row.kind === destination.kind)
            ?.material.id ?? savedId)
  if (complete)
    return (
      <>
        <SheetHead title="선택한 내용을 가져왔어요" />
        <div className="space-y-5">
          <p className="text-c14 leading-relaxed text-t3">
            서버 저장과 편집용 최신 내용 확인을 마쳤어요. 새 작품과 세계관은
            비공개로 생성돼요.
          </p>
          {!!preserved.length && (
            <p role="status" className="text-c14 leading-relaxed text-point">
              {preserved.join(', ')}에 남아 있는 편집도 유지했어요. 편집 화면의
              ‘비교하고 저장’에서 이어갈 수 있어요.
            </p>
          )}
          <ul className="divide-y divide-border">
            {complete.materials.map((row) => (
              <li
                key={row.candidate}
                className="flex min-h-12 items-center justify-between gap-3 text-c14"
              >
                <span>
                  {typeof row.material.name === 'string'
                    ? row.material.name
                    : typeof row.material.title === 'string'
                      ? row.material.title
                      : IMPORT_LABEL[row.kind]}
                </span>
                <span className="flex items-center gap-1 text-c13 text-t3 over10:text-c14">
                  <LuCheck aria-hidden="true" className="size-4 text-ok" />
                  {
                    {
                      create: '새로 생성',
                      reuse: '기존 재료 재사용',
                      update: '선택한 원본 수정',
                    }[row.action]
                  }
                </span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end">
            <Button
              onClick={() => {
                closeTop()
                if (savedId)
                  router.push(
                    `/studio-renew/${destination.kind}/${savedId}/${STEPS[destination.kind][0].id}` as Route
                  )
              }}
            >
              편집으로 돌아가기
            </Button>
          </div>
        </div>
      </>
    )

  return (
    <>
      <SheetHead title="같은 재료가 있는지 확인했어요">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="가져올 내용 수정으로 돌아가기"
          className="order-first -ml-2"
          disabled={busy || frozen}
          onClick={onBack}
        >
          <LuArrowLeft aria-hidden="true" className="size-4" />
        </Button>
      </SheetHead>
      <p className="mb-5 text-c14 leading-relaxed text-t3">
        {phase === 'refreshing'
          ? '서버에 이식은 완료됐어요. 최신 저장본 확인을 마치면 편집을 이어갈 수 있어요.'
          : '설정이 같은 재료는 재사용해요. 이름이 같거나 비슷한 재료는 내용을 비교하고 선택해 주세요. 아직 저장하지 않았어요.'}
      </p>
      <div className="divide-y divide-border">
        {candidates.map((row) => {
          const value = actions[row.candidate] ?? ''
          const options = [
            { value: '', label: '가져오기 방법 선택' },
            { value: 'auto', label: '같은 설정은 재사용, 없으면 생성' },
            { value: 'create', label: '새 재료로 가져오기' },
            ...row.matches.flatMap((match) => [
              {
                value: `reuse:${match.id}`,
                label: `${typeof match.data.name === 'string' ? match.data.name : typeof match.data.title === 'string' ? match.data.title : IMPORT_LABEL[row.kind]} · 기존 내용 재사용`,
              },
              {
                value: `update:${match.id}`,
                label: `${typeof match.data.name === 'string' ? match.data.name : typeof match.data.title === 'string' ? match.data.title : IMPORT_LABEL[row.kind]} · 선택 필드로 수정`,
              },
            ]),
            ...(destination.id && row === root
              ? [
                  {
                    value: `current:${destination.id}`,
                    label: '지금 편집 중인 원본에 선택 필드 반영',
                  },
                ]
              : []),
          ]
          return (
            <div key={row.candidate} className="space-y-3 py-5 first:pt-0">
              <div>
                <p className="text-c12 text-t3 over10:text-c14">
                  {IMPORT_LABEL[row.kind]}
                </p>
                <h3 className="mt-1 text-c16 font-semibold">
                  {typeof row.data.name === 'string'
                    ? row.data.name
                    : typeof row.data.title === 'string'
                      ? row.data.title
                      : IMPORT_LABEL[row.kind]}
                </h3>
              </div>
              <Combobox
                ariaLabel={`${row.candidate} 가져오기 방법`}
                value={value}
                options={options}
                disabled={busy || frozen}
                onChange={(value) =>
                  setActions((state) => ({ ...state, [row.candidate]: value }))
                }
              />
              {row.matches.map((match) => (
                <details
                  key={match.id}
                  className="text-c13 leading-relaxed text-t3 over10:text-c14"
                >
                  <summary className="cursor-pointer py-1">
                    {
                      {
                        exact: '같은 설정',
                        similar: '비슷한 설정',
                        conflict: '같은 이름·다른 설정',
                      }[match.status]
                    }{' '}
                    ·{' '}
                    {typeof match.data.name === 'string'
                      ? match.data.name
                      : typeof match.data.title === 'string'
                        ? match.data.title
                        : IMPORT_LABEL[row.kind]}
                  </summary>
                  {!!match.differences.length && (
                    <p className="mt-2">
                      다른 항목:{' '}
                      {match.differences
                        .map((key) => IMPORT_FIELDS[row.kind][key] ?? key)
                        .join(', ')}
                    </p>
                  )}
                  {match.fieldOrderChanged && <p>상태 항목의 순서가 달라요.</p>}
                  {match.fieldDifferences &&
                    Object.entries(match.fieldDifferences).map(
                      ([field, changes]) => (
                        <p key={field}>
                          {field}: {changes.join(', ')}
                        </p>
                      )
                    )}
                  {Object.entries(match.data)
                    .filter(
                      ([key, value]) =>
                        Object.hasOwn(IMPORT_FIELDS[row.kind], key) &&
                        typeof value === 'string'
                    )
                    .map(([key, value]) => (
                      <div key={key} className="mt-2">
                        <b className="font-medium text-t2">
                          {IMPORT_FIELDS[row.kind][key]}
                        </b>
                        <p className="max-h-40 overflow-auto whitespace-pre-wrap break-words">
                          {String(value)}
                        </p>
                      </div>
                    ))}
                </details>
              ))}
              {!!row.errors.length && (
                <div role="alert" className="space-y-2">
                  {row.errors.map((error) => (
                    <p
                      key={error}
                      className="text-c13 leading-relaxed text-error over10:text-c14"
                    >
                      {error}
                    </p>
                  ))}
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busy || frozen}
                    onClick={onBack}
                  >
                    <LuArrowLeft aria-hidden="true" className="size-4" />
                    {IMPORT_LABEL[row.kind]} 내용 수정
                  </Button>
                </div>
              )}
            </div>
          )
        })}
      </div>
      {destination.kind === 'work' && (
        <div className="space-y-3 border-t border-border py-4">
          <p className="text-c14 text-t3">
            작품에 재료를 연결하고 시작 장면{' '}
            {analysis?.bundle.starts.length ?? 0}개를 함께 가져와요.
          </p>
          {!!candidates.filter((row) => row.kind === 'character').length && (
            <Combobox
              label="주인공"
              value={lead}
              onChange={setLead}
              disabled={busy || frozen}
              options={[
                { value: '', label: '원고의 주인공 설정 유지' },
                ...candidates
                  .filter((row) => row.kind === 'character')
                  .map((row) => ({
                    value: row.candidate,
                    label:
                      typeof row.data.name === 'string'
                        ? row.data.name
                        : '이름 없는 캐릭터',
                  })),
              ]}
            />
          )}
        </div>
      )}
      {updates && (
        <Checkbox
          label="선택한 기존 원본을 수정하는 것을 확인했어요. 다른 작품에서도 쓰는 재료일 수 있어요."
          checked={allowUpdate}
          disabled={busy || frozen}
          onChange={(event) => setAllowUpdate(event.target.checked)}
        />
      )}
      {busy && (
        <p role="status" className="mt-4 text-c14 text-t3">
          {phase === 'refreshing'
            ? '서버 저장은 완료됐어요. 편집할 최신 내용을 불러오고 있어요…'
            : '선택한 내용을 서버에 저장하고 있어요. 이 창을 닫아도 이식하기에서 결과를 이어서 확인할 수 있어요.'}
        </p>
      )}
      <ErrorToast message={error} />
      <div className="mt-5 flex justify-end border-t border-border pt-4">
        <Button
          disabled={!ready && !frozen}
          loading={busy}
          onClick={() => void confirm()}
        >
          <LuImport aria-hidden="true" className="size-4" />
          {frozen ? '저장 결과 다시 확인' : '확인한 내용 가져오기'}
        </Button>
      </div>
    </>
  )
}
